import { useEffect, useMemo, useState } from 'react';
import { Search, Send, UserPlus, X, Clock, Mail, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useApi } from '@/hooks/use-api';

const TEAM_GROUPS = [
  { kind: 'founder', label: 'Founder' },
  { kind: 'cofounder', label: 'Co-founder' },
  { kind: 'employee', label: 'Team member' },
  { kind: 'advisor', label: 'Advisor' },
  { kind: 'investor', label: 'Investor' },
];

const emailOk = (v) => /^\S+@\S+\.\S+$/.test(String(v || '').trim());

/**
 * Unified "Team" panel: one search input drives both flows —
 *   • Match found → picker card + role + kind → Add member
 *   • No match    → collapse straight into an "Invite this person" form
 *                  (email required, name+role optional; email prefilled if the
 *                  search query looked like an address).
 *
 * Pending invites are rendered inside the same section so admins/founders
 * see the full state of team recruitment in one place.
 *
 * Props:
 *   invites        — array of pending invites currently on the venture
 *   onAdd(payload) — resolves an existing user & attaches to the team
 *   onInvite(payload) — sends an email invite
 *   onCancelInvite(id)
 *   testidPrefix   — namespace for data-testids (default: "team")
 */
export const TeamMemberFinder = ({
  invites = [], onAdd, onInvite, onCancelInvite, testidPrefix = 'team',
}) => {
  const api = useApi();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState('');
  const [kind, setKind] = useState('cofounder');
  // Invite form state (used only when no match found)
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteKind, setInviteKind] = useState('cofounder');

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setSearched(false); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const d = await api.get(`/api/venture/users/search?q=${encodeURIComponent(term)}`);
        setResults(d.items || []);
        setSearched(true);
        // If the query looks like an email and no user was found, prefill the invite form.
        if ((!d.items || !d.items.length) && emailOk(term)) setInviteEmail(term);
      } finally { setBusy(false); }
    }, 260);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const showInvite = useMemo(
    () => searched && q.trim().length >= 2 && !results.length && !picked,
    [searched, q, results.length, picked],
  );

  const resetPicker = () => { setPicked(null); setRole(''); setKind('cofounder'); setQ(''); setResults([]); setSearched(false); };
  const resetInvite = () => { setInviteEmail(''); setInviteName(''); setInviteRole(''); setInviteKind('cofounder'); };

  const doAdd = async () => {
    if (!picked || !onAdd) return;
    setBusy(true);
    try { await onAdd({ user_id: picked.id, email: picked.email, name: picked.name, role, kind }); resetPicker(); }
    finally { setBusy(false); }
  };
  const doInvite = async () => {
    if (!onInvite) return;
    setBusy(true);
    try {
      await onInvite({ email: inviteEmail.trim(), name: inviteName.trim(), role: inviteRole.trim(), kind: inviteKind });
      resetInvite(); resetPicker();
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-4 sm:p-5 space-y-4" data-testid={`${testidPrefix}-finder`}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-grotesk text-[11px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-2">
          <UserPlus className="h-3.5 w-3.5" /> Add team member
        </p>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Search Founder OS users by <span className="text-foreground/70">name, email, phone or user ID</span>. If they're not on the platform yet, we'll send an email invite.
      </p>

      {/* Search / picked */}
      {picked ? (
        <>
          <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5" data-testid={`${testidPrefix}-picked`}>
            <Avatar className="h-8 w-8"><AvatarImage src={picked.avatar} /><AvatarFallback className="text-[11px]">{(picked.name || '?')[0]}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm truncate font-medium">{picked.name}</p>
              <p className="text-[11px] text-muted-foreground truncate">{picked.email}{picked.phone ? ` · ${picked.phone}` : ''}</p>
            </div>
            <button type="button" onClick={resetPicker} className="text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-picked-clear`}><X className="h-4 w-4" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-2.5">
            <Input placeholder="Role (e.g. CTO)" value={role} onChange={(e) => setRole(e.target.value)} data-testid={`${testidPrefix}-add-role`} />
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid={`${testidPrefix}-add-kind`}><SelectValue /></SelectTrigger>
              <SelectContent>{TEAM_GROUPS.map((g) => <SelectItem key={g.kind} value={g.kind}>{g.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={doAdd} disabled={busy} className="rounded-full" data-testid={`${testidPrefix}-add-save`}>Add member</Button>
          </div>
        </>
      ) : (
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search Name · Email · Phone · User ID"
            className="pl-9"
            data-testid={`${testidPrefix}-search`}
          />
          {searched && q.trim().length >= 2 && results.length > 0 && (
            <div className="absolute z-30 top-full mt-1 left-0 right-0 rounded-xl border border-border bg-popover shadow-lg overflow-hidden" data-testid={`${testidPrefix}-results`}>
              {results.map((u) => (
                <button key={u.id} type="button" onClick={() => { setPicked(u); setQ(''); setResults([]); setSearched(false); }}
                  className="flex items-center gap-2.5 w-full px-3 py-2 hover:bg-secondary text-left" data-testid={`${testidPrefix}-result-${u.id}`}>
                  <Avatar className="h-7 w-7"><AvatarImage src={u.avatar} /><AvatarFallback className="text-[10px]">{(u.name || '?')[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{u.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invite form appears when the query returns nothing */}
      {showInvite && (
        <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3.5 sm:p-4 space-y-3" data-testid={`${testidPrefix}-invite-block`}>
          <p className="text-xs font-medium flex items-center gap-1.5"><Send className="h-3.5 w-3.5 text-primary" /> Not on Founder OS yet — invite them</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <div>
              <label className="text-[11px] text-muted-foreground">First name (optional)</label>
              <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Aarav" className="mt-1" data-testid={`${testidPrefix}-invite-name`} />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Email <span className="text-red-500">*</span></label>
              <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@email.com" className="mt-1" data-testid={`${testidPrefix}-invite-email`} />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Role (optional)</label>
              <Input value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} placeholder="CTO" className="mt-1" data-testid={`${testidPrefix}-invite-role`} />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Kind</label>
              <Select value={inviteKind} onValueChange={setInviteKind}>
                <SelectTrigger className="mt-1" data-testid={`${testidPrefix}-invite-kind`}><SelectValue /></SelectTrigger>
                <SelectContent>{TEAM_GROUPS.map((g) => <SelectItem key={g.kind} value={g.kind}>{g.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={resetPicker} data-testid={`${testidPrefix}-invite-cancel`}>Cancel</Button>
            <Button size="sm" onClick={doInvite} disabled={!emailOk(inviteEmail) || busy} className="rounded-full gap-1.5" data-testid={`${testidPrefix}-invite-send`}>
              <Send className="h-3.5 w-3.5" /> Send invite
            </Button>
          </div>
        </div>
      )}

      {/* Pending invites — inline so admins see the full team-recruitment state */}
      {invites.length > 0 && (
        <div data-testid={`${testidPrefix}-invites-list`}>
          <p className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2 flex items-center gap-1.5"><Mail className="h-3 w-3" /> Pending invites · {invites.length}</p>
          <div className="space-y-2">
            {invites.map((i) => (
              <div key={i.id} className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-card px-3 py-2.5" data-testid={`${testidPrefix}-invite-row-${i.id}`}>
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="flex-1 text-sm truncate">{i.email}</p>
                {i.role && <span className="text-xs text-muted-foreground truncate">· {i.role}</span>}
                <Badge variant="outline" className="text-[10px] uppercase">{i.kind}</Badge>
                {onCancelInvite && (
                  <button type="button" onClick={() => onCancelInvite(i.id)} className="text-muted-foreground hover:text-red-500" data-testid={`${testidPrefix}-invite-cancel-${i.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberFinder;
